/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import { LocalizedPrice } from 'env/utils/NumbersLocalization';
import withCustomComponent from 'ui/components/withCustomComponent';
import { bookingsFeatures } from 'env/bookingsResources';
import { ResourceAvailableSlots } from 'ui/_pages/bookings/resources/ResourceAvailableSlots';
import BookingDateTimeForm from 'ui/_pages/bookings/details/BookingDateTimeForm';
import BookingServicesForm from './BookingServicesForm';
import Accordion from 'ui/components/Accordion';
import { useResourceQuery } from '../../../../env/hooks/useAllResourcesQuery';
import { usePriceRates } from '../../../../env/hooks/usePriceRates';
import { withRouter } from 'next/router';

const BookingResourceInfo = withCustomComponent('BookingResourceInfo')(
  inject(
    'appStore',
    'bookingsStore',
    'authStore'
  )(
    withRouter(
      observer(
        ({
          componentName,
          isSidePopup,
          bookingsStore,
          appStore,
          authStore,
          updateBooking,
        }) => {
          const [dates, setDates] = useState({});
          const { business, configuration } = appStore;
          const { accessToken, host } = authStore;
          const { t } = useTranslation();

          useEffect(() => {
            if (!bookingsStore.hasLoadedResources)
              bookingsStore.loadResources();
            initToolTips();
          }, []);

          const initToolTips = () => {
            const $ = window.$;
            if ($('[data-toggle="available-tooltip"]').length > 0) {
              $('[data-toggle="available-tooltip"]').tooltip({
                html: true,
              });
            }
          };

          const getCurrentBooking = () => bookingsStore.booking;

          const getResource = () => {
            return (
              bookingsStore.booking &&
              _(bookingsStore.resources)
                .filter((r) => r.Id === bookingsStore.booking.ResourceId)
                .first()
            );
          };

          const selectDates = (selectingDate, date) => {
            const booking = getCurrentBooking();
            let newDates = { ...dates };
            newDates[selectingDate] = moment(date).toDate();

            if (selectingDate === 'fromTime') {
              if (moment(date) >= moment(booking.ToTime)) {
                const smallestDate = moment.min(
                  moment(date).endOf('day'),
                  moment(date).add(60, 'minutes')
                );
                newDates.toTime = smallestDate.toDate();
              }
            }

            if (selectingDate === 'toTime') {
              if (moment(date) <= moment(booking.FromTime)) {
                const greatestDate = moment.max(
                  moment(date).startOf('day'),
                  moment(date).add(-60, 'minutes')
                );
                newDates.fromTime = greatestDate.toDate();
              }
            }

            if (newDates.fromTime) updateBooking('FromTime', newDates.fromTime);
            if (newDates.toTime) updateBooking('ToTime', newDates.toTime);
            setDates(newDates);
          };

          const resource = getResource();
          const resourceQuery = useResourceQuery(
            resource?.Id,
            accessToken,
            host
          );

          const r = resourceQuery.data;
          const priceRates = usePriceRates(r?.ResourceType?.Id);

          const bookingsFeaturesObjectArray =
            resource != undefined
              ? bookingsFeatures(t)
                  .map((feature, i) => {
                    if (resource[feature.key] === true) return feature;
                    return null;
                  })
                  .filter((f) => f != null)
              : null;

          if (!resource)
            return (
              <small className="fs-12 text-gray-600">
                {t('Please select resource to continue booking.')}
              </small>
            );
          if (!bookingsStore.booking.ResourceId) return null;

          const shouldShowSlots = moment(
            bookingsStore.booking.ToTime
          ).isSameOrBefore(
            moment(bookingsStore.booking.FromTime).startOf('day').add(1, 'days')
          );

          const hasShifts =
            resource && resource.Shifts && resource.Shifts.length > 0;

          const pricePerHourLabel = priceRates.data
            ? `£${priceRates.data.price} / Hour`
            : '';
          const priceForHalfAndFullDayShiftLabel =
            priceRates.data && priceRates.data.maximumPrice != null
              ? `£${priceRates.data.price} Half / £${priceRates.data.maximumPrice} Full Day`
              : '';
          const priceLabel = hasShifts
            ? priceForHalfAndFullDayShiftLabel
            : pricePerHourLabel;

          return (
            <div
              data-component-name={componentName}
              className={`booking-resource-info ${isSidePopup ? 'mb-24' : ''}`}
            >
              <div className="booking-resource-info__image">
                <img
                  src={`${business.NativeHomeUrlWithLanguage}/publicresources/getimage/${bookingsStore.booking.ResourceId}?h=480&w=700`}
                  alt={bookingsStore.booking.ResourceName}
                  className="img-fluid d-block mt-12 ml-auto mr-auto"
                />
                {!!priceLabel && (
                  <div className="price-wrapper-booking-modal">
                    <span className="tag">{priceLabel}</span>
                  </div>
                )}
              </div>

              {resource && (
                <div>
                  {shouldShowSlots && (
                    <ResourceAvailableSlots
                      id="booking-modal-as"
                      selectDates={selectDates}
                      fromTime={bookingsStore.booking.FromTime}
                      toTime={bookingsStore.booking.ToTime}
                      resource={resource}
                      isSidePopup={isSidePopup}
                    />
                  )}

                  <ul className="list-tags mt-8 mb-24">
                    {resource.Allocation &&
                      resource.GroupName !== 'Coworking' && (
                        <li
                          className="with-icon-left resource-allocation-label"
                          data-toggle="available-tooltip"
                          title={t('Seating {{count}} person', {
                            count: resource.Allocation,
                          })}
                        >
                          <i className={`icon-${'user'}`}></i>
                          {'x'}
                          {resource.Allocation}
                        </li>
                      )}
                    {resource.Price > 0 && (
                      <li
                        className="with-icon-left resource-price-label"
                        data-toggle="available-tooltip"
                        title={t('Price')}
                      >
                        <i className="icon-money"></i>
                        <LocalizedPrice
                          currency={business.Currency.Code}
                          amount={resource.Price}
                        />
                      </li>
                    )}
                    {resource.ResourceTypeName && (
                      <li className="resource-type-name-label">
                        {resource.ResourceTypeName}
                      </li>
                    )}
                    {resource.GroupName && (
                      <li className="resource-group-label">
                        {resource.GroupName}
                      </li>
                    )}
                  </ul>

                  {isSidePopup && (
                    <BookingDateTimeForm
                      updateBooking={(prop, value) =>
                        updateBooking(prop, value)
                      }
                      withBorder
                      isSidePopup
                    />
                  )}
                </div>
              )}

              {isSidePopup && (
                <BookingServicesForm
                  loadBookingPrice={bookingsStore.loadBookingPrice}
                  isSidePopup
                />
              )}

              {isSidePopup ? (
                <>
                  <Accordion
                    id="description-section"
                    heading={t('Description')}
                    showDividerTop={false}
                    key="accordion-description-section"
                  >
                    <div
                      className="booking-resource-info--description mb-20"
                      dangerouslySetInnerHTML={{ __html: resource.Description }}
                    ></div>
                  </Accordion>

                  <Accordion
                    id="features-section"
                    heading={t('Features')}
                    showDividerTop={false}
                    key="accordion-features-section"
                  >
                    {bookingsFeaturesObjectArray.length > 0 && (
                      <>
                        <div className="">
                          <ul className="list-grid list-grid--33 list-grid--sm-50 mb-20">
                            {bookingsFeaturesObjectArray.map((feature, i) => (
                              <li key={i}>
                                <i className={`${feature.icon} fs-40`}></i>
                                <div className="mt-8">{feature.name}</div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </>
                    )}
                  </Accordion>
                </>
              ) : (
                <>
                  <div
                    className="booking-resource-info--description mt-20"
                    dangerouslySetInnerHTML={{ __html: resource.Description }}
                  ></div>

                  <hr className="mt-32 mb-24" />

                  {bookingsFeaturesObjectArray.length > 0 && (
                    <>
                      <div className="">
                        <h5 className="mb-20">{t('Features')}</h5>
                        <ul className="list-grid list-grid--33 list-grid--sm-50">
                          {bookingsFeaturesObjectArray.map((feature, i) => (
                            <li key={i}>
                              <i className={`${feature.icon} fs-40`}></i>
                              <div className="mt-8">{feature.name}</div>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <hr className="mt-32 mb-24" />
                    </>
                  )}
                </>
              )}
            </div>
          );
        }
      )
    )
  )
);

export default BookingResourceInfo;
